import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import IcCheckBold from 'src/assets/images/icCheckBold.svg';
import IcChevronRightBold from 'src/assets/images/icChevronRightBold.svg';
import IcMinusBold from 'src/assets/images/icMinusBold.svg';
import BenefitDetailModal from 'src/components/AzarOnly/Subscription/BenefitDetailModal';
import type { SubscriptionGuideLandFrom } from 'src/components/AzarOnly/Subscription/SubscriptionGuideModal/constants';
import { useSubscriptionEventData } from 'src/hooks/useSubscription';
import { showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import type { BenefitRow, PlanColumn } from './buildBenefitTableData';
const Container = styled.div`
  position: relative;
`;
const ColumnOverlay = styled.div<{
  $selectedIndex: number;
  $totalColumns: number;
}>`
  position: absolute;
  top: 0px;
  bottom: 0px;
  width: 56px;
  background-color: ${({
  theme
}) => theme.color.content2};
  border-radius: 12px;
  inset-inline-end: 17px;
  transition: transform 0.2s ease-in-out;
  transform: translateX(
    ${({
  $selectedIndex,
  $totalColumns,
  theme
}) => {
  const columnWidth = 57;
  const offset = ($totalColumns - 1 - $selectedIndex) * columnWidth;
  return theme.isRtl ? `${offset}px` : `${-offset}px`;
}}
  );
  z-index: 0;
  will-change: transform;
`;
const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: 40px;
`;
const HeaderTitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding-block: 12px;
  padding-inline-start: 20px;
  padding-inline-end: 16px;
  min-width: 0;
`;
const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  color: ${({
  theme
}) => theme.color.content9};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;
const HeaderCheckArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 128px;
  padding-inline-end: 20px;
  gap: 4px;
`;
const HeaderCheckLabel = styled.button<{
  $isHighPlan: boolean;
}>`
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  background: none;
  border: none;
  padding: 12px 0;
  cursor: pointer;
  transition: color 0.2s ease;
  color: ${({
  $isHighPlan,
  theme
}) => $isHighPlan ? theme.color.primary1 : theme.color.content12};
`;
const FeatureRow = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: 44px;
`;
const FeatureContent = styled.div<{
  $clickable: boolean;
}>`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-block: 12px;
  padding-inline-start: 20px;
  padding-inline-end: 16px;
  cursor: ${({
  $clickable
}) => $clickable ? 'pointer' : 'default'};
`;
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 4px;
  background-color: ${({
  theme
}) => theme.color.surface4};
  border-radius: 8px;
  flex-shrink: 0;
`;
const FeatureIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 100px;
  object-fit: cover;
  ${({
  theme
}) => theme.isRtl && `transform: scaleX(-1);`}
`;
const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-width: 0;
`;
const FeatureTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: ${({
  theme
}) => theme.color.content12};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;
const FeatureDescription = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content9};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;
const ChevronIcon = styled(IcChevronRightBold)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${({
  theme
}) => theme.color.content7};
  ${({
  theme
}) => theme.isRtl && `transform: scaleX(-1);`}
`;
const CheckArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 128px;
  padding-inline-end: 20px;
  gap: 4px;
`;
const CheckIconWrapper = styled.button`
  height: 100%;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 12px 0;
  cursor: pointer;
`;
const CheckIcon = styled(IcCheckBold)<{
  $highlighted: boolean;
}>`
  width: 18px;
  height: 18px;
  transition: color 0.2s ease;
  color: ${({
  $highlighted,
  theme
}) => $highlighted ? theme.color.primary1 : theme.color.contentDeep};
`;
const MinusIcon = styled(IcMinusBold)`
  width: 18px;
  height: 18px;
  color: ${({
  theme
}) => theme.color.content6};
`;
interface Props {
  planColumns: PlanColumn[];
  benefitRows: BenefitRow[];
  defaultPlanId?: string;
  selectedPlanId?: string;
  initialFeature?: string;
  landFrom: SubscriptionGuideLandFrom;
  onPlanSelect: (id: string) => void;
  className?: string;
}
const SubscriptionFeatureList = ({
  defaultPlanId,
  selectedPlanId,
  initialFeature,
  landFrom,
  planColumns,
  benefitRows,
  onPlanSelect,
  className
}: Props) => {
  const {
    t
  } = useTranslation();
  const showModal = useSetAtom(showModalAtom);
  const subscriptionEventData = useSubscriptionEventData();
  const selectedPlanIndex = !selectedPlanId ? 0 : Math.max(0, planColumns.findIndex(c => c.id === selectedPlanId));
  const handleBenefitClick = (benefit: BenefitRow) => {
    if (!benefit.guide) return;
    const {
      guide
    } = benefit;
    showModal({
      key: ModalType.SUBSCRIPTION_BENEFIT_DETAIL,
      component: () => <BenefitDetailModal title={guide.title} description={guide.description} imageUrl={guide.imageUrl} />
    });
    pushEvent({
      eventType: EVENT_TYPE.SUBSCRIPTION,
      eventName: EVENT_NAME.SUBS__CLICK_DETAIL_GUIDE,
      eventParams: {
        benefit_info: benefit.id,
        current_page_id: selectedPlanId,
        initial_feature: initialFeature ?? 'none',
        initial_page_id: defaultPlanId ?? selectedPlanId,
        land_from: landFrom,
        ...subscriptionEventData.eventParams,
        is_store_account_active: false,
        is_store_account_matched: false
      }
    });
  };
  return <Container className={className} data-sentry-element="Container" data-sentry-component="SubscriptionFeatureList" data-sentry-source-file="SubscriptionFeatureList.tsx">
      <ColumnOverlay $selectedIndex={selectedPlanIndex} $totalColumns={planColumns.length || 1} data-sentry-element="ColumnOverlay" data-sentry-source-file="SubscriptionFeatureList.tsx" />
      <HeaderRow data-sentry-element="HeaderRow" data-sentry-source-file="SubscriptionFeatureList.tsx">
        <HeaderTitleWrapper data-sentry-element="HeaderTitleWrapper" data-sentry-source-file="SubscriptionFeatureList.tsx">
          <HeaderTitle data-sentry-element="HeaderTitle" data-sentry-source-file="SubscriptionFeatureList.tsx">{t('subs_info_benefits')}</HeaderTitle>
        </HeaderTitleWrapper>
        <HeaderCheckArea data-sentry-element="HeaderCheckArea" data-sentry-source-file="SubscriptionFeatureList.tsx">
          {planColumns.map(col => <HeaderCheckLabel key={col.id} $isHighPlan={col.isHighPlan} onClick={() => onPlanSelect(col.id)}>
              {col.label}
            </HeaderCheckLabel>)}
        </HeaderCheckArea>
      </HeaderRow>
      {benefitRows.map(benefit => <FeatureRow key={benefit.id}>
          <FeatureContent $clickable={!!benefit.guide} role={benefit.guide ? 'button' : undefined} tabIndex={benefit.guide ? 0 : undefined} onClick={() => handleBenefitClick(benefit)}>
            {benefit.iconSrc && <IconWrapper>
                <FeatureIcon src={benefit.iconSrc} alt='' />
              </IconWrapper>}
            <TitleArea>
              <FeatureTitle>{benefit.title}</FeatureTitle>
              {benefit.description && <FeatureDescription>{benefit.description}</FeatureDescription>}
            </TitleArea>
            {benefit.guide && <ChevronIcon />}
          </FeatureContent>
          <CheckArea>
            {planColumns.map(col => <CheckIconWrapper key={col.id} onClick={e => {
          e.stopPropagation();
          onPlanSelect(col.id);
        }}>
                {benefit.includedByPlan[col.id] ? <CheckIcon $highlighted={col.isHighPlan && selectedPlanId === col.id} /> : <MinusIcon />}
              </CheckIconWrapper>)}
          </CheckArea>
        </FeatureRow>)}
    </Container>;
};
export default SubscriptionFeatureList;